import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  History, 
  Trash2, 
  Star, 
  Calendar,
  Target,
  Hash,
  TrendingUp,
  ShieldAlert
} from "lucide-react";

const API = '/api';

export default function ScoreHistory() {
  const { isAdmin, getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedShooter, setSelectedShooter] = useState("");
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roundToDelete, setRoundToDelete] = useState(null);
  const [viewRound, setViewRound] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedShooter) {
      fetchRounds();
    }
  }, [selectedShooter, selectedEvent]);

  const fetchInitialData = async () => {
    try {
      const t = Date.now();
      const [eventsRes, shootersRes] = await Promise.all([
        axios.get(`${API}/events?t=${t}`),
        axios.get(`${API}/shooters?t=${t}`),
      ]);
      setEvents(eventsRes.data);
      setShooters(shootersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRounds = async () => {
    try {
      let url = `${API}/rounds?shooter_id=${selectedShooter}&t=${Date.now()}`;
      if (selectedEvent) {
        url += `&event_id=${selectedEvent}`;
      }
      const res = await axios.get(url);
      // Sort by date descending
      const sorted = res.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setRounds(sorted);
    } catch (error) {
      toast.error("Failed to fetch rounds");
    }
  };

  const handleDeleteRound = async () => {
    if (!roundToDelete) return;
    
    try {
      await axios.delete(`${API}/rounds/${roundToDelete.id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Score deleted");
      setDeleteDialogOpen(false);
      setRoundToDelete(null);
      setTimeout(() => fetchRounds(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete score");
    }
  };

  const openDeleteDialog = (round) => {
    setRoundToDelete(round);
    setDeleteDialogOpen(true);
  };

  const getShooterName = (shooterId) => {
    const shooter = shooters.find(s => s.id === shooterId);
    return shooter?.name || "Unknown";
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.name || "Unknown";
  };

  if (!isAdmin()) {
    return (
      <div data-testid="score-history-page" className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Score History
          </h1>
          <p className="text-slate-500 mt-1">View and manage all scores</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldAlert className="mx-auto text-amber-500 mb-4" size={48} />
            <h3 className="font-semibold text-lg mb-2">Admin Access Required</h3>
            <p className="text-slate-500">
              Only administrators can view and manage score history.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="score-history-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Score History
        </h1>
        <p className="text-slate-500 mt-1">View and manage all scores by shooter</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Shooter</label>
              <Select value={selectedShooter} onValueChange={setSelectedShooter}>
                <SelectTrigger data-testid="shooter-select">
                  <SelectValue placeholder="Choose a shooter" />
                </SelectTrigger>
                <SelectContent>
                  {shooters.map((shooter) => (
                    <SelectItem key={shooter.id} value={shooter.id}>
                      {shooter.shooter_id && (
                        <span className="font-mono text-xs mr-2">[{shooter.shooter_id}]</span>
                      )}
                      {shooter.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Event (optional)</label>
              <Select value={selectedEvent || "all"} onValueChange={(val) => setSelectedEvent(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rounds List */}
      {selectedShooter && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <History className="text-[#1B3B6F]" />
              Score History for {getShooterName(selectedShooter)}
              <Badge variant="outline">{rounds.length} rounds</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {rounds.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No scores recorded for this shooter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event</th>
                      <th className="text-center">Score</th>
                      <th className="text-center">Longest Run</th>
                      <th className="text-center">Best?</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rounds.map((round) => (
                      <tr key={round.id} className={round.is_best_round ? "bg-yellow-50" : ""}>
                        <td className="text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDateTime(round.created_at).split(' ')[0]}
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatDateTime(round.created_at).split(' ')[1]}
                          </div>
                        </td>
                        <td className="font-medium">{getEventName(round.event_id)}</td>
                        <td className="text-center">
                          <span className="font-mono text-xl font-bold text-green-700">
                            {round.total_hits}
                          </span>
                          <span className="text-slate-400">/{round.scores?.length || 25}</span>
                        </td>
                        <td className="text-center">
                          <span className="font-mono text-lg text-orange-600">
                            {round.longest_streak}
                          </span>
                        </td>
                        <td className="text-center">
                          {round.is_best_round && (
                            <Star className="mx-auto text-yellow-500 fill-yellow-500" size={20} />
                          )}
                        </td>
                        <td className="text-center">
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setViewRound(round)}
                            >
                              <Target size={14} className="mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => openDeleteDialog(round)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* View Round Dialog */}
      <Dialog open={!!viewRound} onOpenChange={() => setViewRound(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Round Details</DialogTitle>
            <DialogDescription>
              {viewRound && (
                <>
                  {getShooterName(viewRound.shooter_id)} - {getEventName(viewRound.event_id)}
                  <br />
                  {formatDateTime(viewRound.created_at)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {viewRound && (() => {
            // Parse scores array properly
            const scoresArray = viewRound.scores 
              ? (typeof viewRound.scores === 'string' ? JSON.parse(viewRound.scores) : viewRound.scores)
              : [];
            const totalHits = viewRound.total_hits ?? scoresArray.filter(s => s === true || s === 1).length;
            const totalMisses = scoresArray.length - totalHits;
            const longestStreak = viewRound.longest_streak ?? 0;
            
            return (
            <div className="mt-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Hits</p>
                  <p className="font-mono text-2xl font-bold text-green-700">
                    {totalHits}/{scoresArray.length || 25}
                  </p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">Misses</p>
                  <p className="font-mono text-2xl font-bold text-red-700">
                    {totalMisses}
                  </p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600">Longest Run</p>
                  <p className="font-mono text-2xl font-bold text-orange-700">
                    {longestStreak}
                  </p>
                </div>
              </div>

              {/* Shot Grid */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Shot by Shot</p>
                {scoresArray.length > 0 ? (
                  <>
                    <div className="flex flex-wrap gap-1">
                      {scoresArray.map((hit, idx) => (
                        <div
                          key={idx}
                          className={`w-8 h-8 rounded flex items-center justify-center text-xs font-mono
                            ${(hit === true || hit === 1) 
                              ? "bg-green-100 text-green-700 border border-green-300" 
                              : "bg-red-100 text-red-700 border border-red-300"
                            }`}
                        >
                          {idx + 1}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 text-xs text-slate-500 mt-2">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                        Hit
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                        Miss
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-400">No shot data available</p>
                )}
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Score?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this round score. This action cannot be undone.
              {roundToDelete && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p>Score: {roundToDelete.total_hits}/{roundToDelete.scores?.length || 25}</p>
                  <p>Date: {formatDateTime(roundToDelete.created_at)}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRound}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
